import React, { useCallback, useEffect, useState } from "react";

import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack
} from "@mui/material";
import { useAccountSelector } from "store";

type Props = {
  from: string | null;
  to: string | null;
  setFrom: (value: string | null) => void;
  setTo: (value: string | null) => void;
  fromInception: boolean;
  setFromInception: (value: boolean) => void;
  useAccountingDate: boolean;
  setUseAccountingDate: (value: boolean) => void;
  fromLabel: string;
  toLabel: string;
};

const quarters = ["Q1", "Q2", "Q3", "Q4"];
const currentYear = new Date().getUTCFullYear();
const years = Array.from({ length: 54 }, (_, i) => currentYear - i);

const selectStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px"
  },
  "& .MuiSelect-select": {
    padding: "8px !important"
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7B92A9"
  }
};

export const ManualQuarterRange = ({
  from,
  to,
  setFrom,
  setTo,
  fromInception,
  setFromInception,
  useAccountingDate,
  setUseAccountingDate
}: Props) => {
  const isRangeSwitchEnabled = useAccountSelector()?.allow_use_date_range_switch || false;
  const [yearFrom, setYearFrom] = useState<number | null>(null);
  const [yearTo, setYearTo] = useState<number | null>(null);
  const [quarterFrom, setQuarterFrom] = useState<string>("Q1");
  const [quarterTo, setQuarterTo] = useState<string>("Q1");

  const getQuarterDate = useCallback((year: number, quarter: string, isEnd?: boolean) => {
    const month = quarters.indexOf(quarter) * 3 + (isEnd ? 2 : 0);
    const date = isEnd ? new Date(Date.UTC(year, month + 1, 0)) : new Date(Date.UTC(year, month, 1));
    return date.toISOString().split("T")[0];
  }, []);

  const getQuarterFromDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const month = date.getUTCMonth();
    const quarterIndex = Math.floor(month / 3);
    return quarters[quarterIndex];
  }, []);

  const getYearFromDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.getUTCFullYear();
  }, []);

  // Helper function to check if a year/quarter is before the from selection
  const isBeforeFromSelection = useCallback(
    (year: number, quarter: string) => {
      if (fromInception || !yearFrom || !quarterFrom) {
        return false;
      }

      if (year < yearFrom) return true;
      if (year > yearFrom) return false;

      // Same year, compare quarters
      const fromQuarterIndex = quarters.indexOf(quarterFrom);
      const quarterIndex = quarters.indexOf(quarter);
      return quarterIndex < fromQuarterIndex;
    },
    [fromInception, yearFrom, quarterFrom]
  );

  // Parse initial from and to dates when component mounts
  useEffect(() => {
    if (from && !fromInception) {
      const year = getYearFromDate(from);
      const quarter = getQuarterFromDate(from);
      setYearFrom(year);
      setQuarterFrom(quarter);
    }

    if (to) {
      const year = getYearFromDate(to);
      const quarter = getQuarterFromDate(to);
      setYearTo(year);
      setQuarterTo(quarter);
    }
  }, [from, to, fromInception, getYearFromDate, getQuarterFromDate]);

  const handleYearFromChange = (year: number | null) => {
    setYearFrom(year);
    if (year && quarterFrom) {
      setFrom(getQuarterDate(year, quarterFrom, false));
    } else {
      setFrom(null);
    }
  };

  const handleQuarterFromChange = (quarter: string) => {
    setQuarterFrom(quarter);
    if (yearFrom && quarter) {
      setFrom(getQuarterDate(yearFrom, quarter, false));
    }
  };

  const handleYearToChange = (year: number | null) => {
    setYearTo(year);
    if (year && quarterTo) {
      setTo(getQuarterDate(year, quarterTo, true));
    } else {
      setTo(null);
    }
  };

  const handleQuarterToChange = (quarter: string) => {
    setQuarterTo(quarter);
    if (yearTo && quarter) {
      setTo(getQuarterDate(yearTo, quarter, true));
    }
  };

  return (
    <Stack gap="10px">
      <Stack gap="10px" flexDirection="row" alignItems="center">
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl sx={{ width: 100 }}>
            <InputLabel>Year From</InputLabel>
            <Select
              value={yearFrom}
              label="Year From"
              onChange={(e) => handleYearFromChange(e.target.value as number)}
              disabled={fromInception}
              sx={selectStyles}
              renderValue={(value) => value || "YYYY"}
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ width: 75 }}>
            <InputLabel>Quarter</InputLabel>
            <Select
              value={quarterFrom}
              label="Quarter"
              onChange={(e) => handleQuarterFromChange(e.target.value)}
              disabled={fromInception}
              renderValue={(value) => value || "Q"}
              sx={selectStyles}
            >
              {quarters.map((q) => (
                <MenuItem key={q} value={q}>
                  {q}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        -
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl sx={{ width: 100 }}>
            <InputLabel>Year To</InputLabel>
            <Select
              value={yearTo}
              label="Year To"
              onChange={(e) => handleYearToChange(e.target.value as number)}
              sx={selectStyles}
              renderValue={(value) => value || "YYYY"}
            >
              {years.map((year) => (
                <MenuItem key={year} value={year} disabled={isBeforeFromSelection(year, "Q1")}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ width: 75 }}>
            <InputLabel>Quarter</InputLabel>
            <Select
              value={quarterTo}
              label="Quarter"
              onChange={(e) => handleQuarterToChange(e.target.value)}
              sx={selectStyles}
              renderValue={(value) => value || "Q"}
            >
              {quarters.map((q) => (
                <MenuItem key={q} value={q} disabled={yearTo ? isBeforeFromSelection(yearTo, q) : false}>
                  {q}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>
      <FormControlLabel
        control={<Checkbox checked={fromInception} onChange={(e) => setFromInception(e.target.checked)} />}
        label="From Inception"
      />
      {isRangeSwitchEnabled && (
        <>
          <Divider sx={{ borderColor: "#DBE8F5" }} />
          <FormControl>
            <RadioGroup row value={useAccountingDate} onChange={(e) => setUseAccountingDate(e.target.value === "true")}>
              <FormControlLabel value={false} control={<Radio />} label="Effective Date" />
              <FormControlLabel value={true} control={<Radio />} label="Accounting Date" />
            </RadioGroup>
          </FormControl>
        </>
      )}
    </Stack>
  );
};
